/* eslint-disable arrow-body-style */
/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import {
  formatNumberAsMoney,
  isString,
  mapObject,
  mergeClasses,
} from "@huub-web/utils";
import { useTranslation } from "react-i18next";
import {
  type HTMLAttributes,
  Fragment,
  type ReactNode,
  type FC,
  type Ref,
} from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Line,
  LineChart,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import { Flex } from "../Flex";

import {
  containerStyle,
  legendItemStyle,
  legendStyle,
  tooltipBulletStyle,
  tooltipListItemNameStyle,
  tooltipListItemStyle,
  tooltipListStyle,
} from "./Graph.css";
import { theme } from "../../theme/css/theme.css";
import { typography } from "../../theme/css/typography.css";
import { tooltipStyle } from "../Tooltip/Tooltip.css";

type FormatType = "string" | "number" | "money";

export type GraphProps = {
  title?: string;
  data: (Record<string, number | string | ReactNode> & {
    label?: string;
    tooltip?: ReactNode;
  })[];
  entries?: Record<
    string,
    {
      label: string;
      tooltip?: ReactNode;
      /**
       * Used for composed chart type
       */
      type?: "area" | "bar" | "line";
    }
  >;
  valueFormat?: FormatType;
  type?:
    | "stackedAreaChart"
    | "stackedBarChart"
    | "lineChart"
    | "lineChartPlain"
    | "pieChart"
    | "composedChart"
    | "percentAreaChart";
  yAxisDomain?: [number, number];
};

const colors = [
  theme.colors.gradientA,
  theme.colors.gradientC,
  theme.colors.gradientB,
  theme.colors.gradientD,
];

const format = (
  type: FormatType,
  value: number | string,
  asPercentage?: boolean,
): string => {
  let display = value;

  if (asPercentage) {
    display = `${(isString(value) ? parseFloat(value) : value) * 100}%`;
  } else if (type === "money") {
    display = formatNumberAsMoney(isString(value) ? parseFloat(value) : value);
  }

  return `${display}`;
};

const getPercentage = (value: number, total: number) => {
  const ratio = total > 0 ? value / total : 0;

  return `${(ratio * 100).toFixed(2)}%`;
};

/**
 * A component that displays a graph.
 * @todo Create individual components for each type of graph
 */
export const Graph: FC<
  GraphProps & HTMLAttributes<HTMLDivElement> & { ref?: Ref<HTMLDivElement> }
> = ({
  data,
  valueFormat = "string",
  entries,
  type = "stackedAreaChart",
  yAxisDomain,
  className,
  ref,
  ...props
}) => {
  const { t } = useTranslation();

  const keys = Object.keys(data?.[0] ?? {}).filter(
    (key) => key !== "label" && key !== "tooltip",
  );

  const pieData = (() => {
    if (type !== "pieChart") {
      return [];
    }

    return data.map((entry) => {
      const entryData = mapObject(entry, ([key, value]) =>
        key === "label" ? undefined : { name: key, value },
      ).filter((entry) => entry !== undefined);

      return entryData;
    });
  })();

  return (
    <>
      <div
        ref={ref}
        className={mergeClasses(containerStyle, typography.bodyXS, className)}
        {...props}
      >
        {(type === "stackedAreaChart" || type === "percentAreaChart") && (
          <ResponsiveContainer>
            <AreaChart
              width={500}
              height={400}
              data={data}
              stackOffset={type === "percentAreaChart" ? "expand" : undefined}
              margin={{
                top: 0,
                right: 20,
                left: 20,
                bottom: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" angle={-45} textAnchor="end" />
              <YAxis
                tickFormatter={(value) =>
                  format(valueFormat, value, type === "percentAreaChart")
                }
                domain={yAxisDomain}
              />
              <Tooltip
                content={
                  <CustomTooltip
                    valueFormat={valueFormat}
                    asPercentage={type === "percentAreaChart"}
                    entries={entries}
                  />
                }
              />

              {keys.map((key, i) => (
                <Area
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stackId="1"
                  stroke={colors?.[i]}
                  fill={colors?.[i]}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}

        {type === "stackedBarChart" && (
          <ResponsiveContainer>
            <BarChart
              width={500}
              height={400}
              data={data}
              stackOffset="sign"
              margin={{
                top: 0,
                right: 20,
                left: 20,
                bottom: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" angle={-45} textAnchor="end" />
              <YAxis
                tickFormatter={(value) => format(valueFormat, value)}
                domain={yAxisDomain}
              />
              <Tooltip
                content={
                  <CustomTooltip valueFormat={valueFormat} entries={entries} />
                }
              />

              {keys.map((key, i) => (
                <Bar
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stackId="1"
                  fill={colors?.[i]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}

        {type === "lineChart" && (
          <ResponsiveContainer>
            <LineChart
              width={500}
              height={400}
              data={data}
              margin={{
                top: 0,
                right: 20,
                left: 20,
                bottom: 20,
              }}
              stackOffset="sign"
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" angle={-45} textAnchor="end" />
              <YAxis
                tickFormatter={(value) => format(valueFormat, value)}
                domain={yAxisDomain}
              />
              <Tooltip
                content={
                  <CustomTooltip valueFormat={valueFormat} entries={entries} />
                }
              />

              {keys.map((key, i) => (
                <Line
                  key={key}
                  dataKey={key}
                  stroke={colors?.[i]}
                  fill={colors?.[i]}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}

        {type === "lineChartPlain" && (
          <ResponsiveContainer>
            <AreaChart
              width={500}
              height={400}
              data={data}
              margin={{
                top: 5,
                right: 5,
                left: 10,
                bottom: 5,
              }}
            >
              <defs>
                {keys.map((key) => (
                  <linearGradient
                    key={key}
                    id={`gradient-${key}`}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#5F6368" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#5F6368" stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                angle={-45}
                textAnchor="end"
                padding={{ left: 10, right: 10 }}
              />
              <YAxis
                domain={yAxisDomain}
                axisLine={false}
                tickLine={false}
                padding={{ top: 10, bottom: 10 }}
                tickFormatter={(value) => format(valueFormat, value)}
              />
              <Tooltip
                content={
                  <CustomTooltip
                    valueFormat={valueFormat}
                    entries={entries}
                    hideBullet={keys.length <= 1}
                  />
                }
              />

              {keys.map((key) => (
                <Area
                  key={key}
                  type="linear"
                  dataKey={key}
                  stroke="#5F6368"
                  strokeWidth={2}
                  fill={`url(#gradient-${key})`}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}

        {type === "composedChart" && (
          <ResponsiveContainer>
            <ComposedChart
              width={500}
              height={400}
              data={data}
              margin={{
                top: 0,
                right: 20,
                left: 20,
                bottom: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" angle={-45} textAnchor="end" />
              <YAxis
                tickFormatter={(value) => format(valueFormat, value)}
                domain={yAxisDomain}
              />
              <Tooltip
                content={
                  <CustomTooltip valueFormat={valueFormat} entries={entries} />
                }
              />

              {keys.map((key, i) => (
                <Fragment key={key}>
                  {entries?.[key]?.type === "area" && (
                    <Area
                      key={key}
                      dataKey={key}
                      type="monotone"
                      stroke={colors?.[i]}
                      fill={colors?.[i]}
                    />
                  )}
                  {entries?.[key]?.type === "line" && (
                    <Line
                      key={key}
                      dataKey={key}
                      stroke={colors?.[i]}
                      fill={colors?.[i]}
                    />
                  )}
                  {!["line", "area"].includes(entries?.[key]?.type ?? "") && (
                    <Bar
                      key={key}
                      dataKey={key}
                      type="monotone"
                      stroke={colors?.[i]}
                      fill={colors?.[i]}
                    />
                  )}
                </Fragment>
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        )}

        {type === "pieChart" && pieData && pieData[0] && (
          <ResponsiveContainer>
            <PieChart
              width={500}
              margin={{
                top: 0,
                right: 20,
                left: 20,
                bottom: 0,
              }}
            >
              <Pie
                data={pieData[0]}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={20}
                outerRadius={80}
                label={{ fill: theme.colors.placeholder }}
                // label={({
                //   cx,
                //   cy,
                //   midAngle,
                //   innerRadius,
                //   outerRadius,
                //   percent,
                // }) => {
                //   const RADIAN = Math.PI / 180;
                //   const radius =
                //     innerRadius + (outerRadius - innerRadius) * 0.5;
                //   const x = cx + radius * Math.cos(-midAngle * RADIAN);
                //   const y = cy + radius * Math.sin(-midAngle * RADIAN);

                //   return (
                //     <text
                //       x={x}
                //       y={y}
                //       fill={theme.colors.onSecondaryAlt}
                //       fontSize={14}
                //     >
                //       {`${(percent * 100).toFixed(0)}%`}
                //     </text>
                //   );
                // }}
              >
                {pieData[0].map((_entry, i) => (
                  <Cell key={`cell-${i}`} fill={colors?.[i]} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>

      {keys && keys.length > 1 && (
        <Flex className={legendStyle} align="center" justify="center">
          {keys.map((key, i) => (
            <Flex
              key={key}
              align="center"
              className={mergeClasses(legendItemStyle, typography.bodyS)}
            >
              <div
                className={mergeClasses(tooltipBulletStyle)}
                style={{ background: colors?.[i] }}
              />
              {entries?.[key]?.label ?? t(`graphs.${key}`)}
            </Flex>
          ))}
        </Flex>
      )}
    </>
  );
};

const CustomTooltip = ({
  valueFormat,
  entries,
  active,
  payload,
  label,
  asPercentage,
  hideBullet = false,
}: {
  valueFormat: FormatType;
  entries: GraphProps["entries"];
  active?: boolean;
  asPercentage?: boolean;
  payload?: any[];
  label?: string;
  hideBullet?: boolean;
}) => {
  const { t } = useTranslation();

  if (active && payload && payload.length) {
    const total = payload.reduce(
      (result, entry) => result + parseFloat(entry.value),
      0,
    );

    return (
      <Flex
        className={mergeClasses(tooltipStyle(), tooltipListStyle)}
        direction="column"
        align="flex-start"
        gap="xs"
      >
        {label && (
          <div
            className={mergeClasses(typography.bodyS)}
            style={{ fontWeight: 600 }}
          >
            {label}
          </div>
        )}

        {payload.map((item: any, i: number) => {
          if (item.payload.tooltip) {
            return <Fragment key={i}>{item.payload.tooltip}</Fragment>;
          }

          return (
            <Flex key={i} className={mergeClasses(tooltipListItemStyle)}>
              {!hideBullet && (
                <div
                  className={mergeClasses(tooltipBulletStyle)}
                  style={{ background: item.fill ?? item.stroke }}
                />
              )}
              <div className={mergeClasses(tooltipListItemNameStyle)}>
                {entries?.[item.name]?.label ?? t(`graphs.${item.name}`)}
              </div>
              <div>
                {format(valueFormat, item.value)}
                {asPercentage
                  ? `(${getPercentage(parseFloat(item.value), total)})`
                  : ""}
              </div>
            </Flex>
          );
        })}
      </Flex>
    );
  }

  return null;
};
